import React, { useEffect, useRef } from 'react';
import {
  FiUpload,
  FiImage,
  FiFileText,
  FiSend,
  FiX,
  FiMinus,
} from 'react-icons/fi';
import './Copilot.css';
import liwazaLogoWhiteShort from '../../assets/liwaza-short-logo-white.png';
import {
  API_BASE_URL,
  API_SOCKET_URL,
  getAuthHeaders,
  updateCopilotMessages,
  loadCopilotConversation,
  redirectToLoginWithReturn,
} from '../../utils/utils';
import {
  createAuthenticatedWebSocket,
  getStoredUser,
  hasAuthenticatedSession,
} from '../../utils/authSession';
import { renderCopilotMarkdownToSafeHtml } from '../../utils/copilotMarkdown';
import 'katex/dist/katex.min.css';

function convertCustomTextToHTML(input) {
  return renderCopilotMarkdownToSafeHtml(input);
}

const Copilot = ({
  messages = [],
  generating,
  generatingText,
  textFading = false,
  context = '',
  setContext = () => {},
  aiContext = '',
  handleSend,
  handleFileUpload,
  handleImageUpload,
  selectedFile,
  selectedImage,
  messagesEndRef,
  onClose, // function to hide the panel
  showFileUpload,
  showImageUpload,
  lgSize,
  setMessages,
  setGenerating,
  userInfo = null,
  currentPage = null,
  sessionContext = {},
}) => {
  const wsRef = useRef(null);

  // Enhanced setMessages function that also saves to localStorage using utils
  const updateMessages = (updateFunction) => {
    const sessionUser = getStoredUser();
    const prioritySchool =
      localStorage.getItem('selectedPrioritySchool') || 'general';

    if (setMessages && sessionUser?.email) {
      updateCopilotMessages(
        setMessages,
        updateFunction,
        sessionUser.email,
        prioritySchool,
        currentPage,
        null, // step
        userInfo
      );
    } else if (setMessages) {
      setMessages(updateFunction);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedContext = String(context || '').trim();
    if (!normalizedContext) return;
    const normalizedAiContext = String(aiContext || '').trim();

    // Add user message to chat immediately
    const userMessage = { role: 'user', content: normalizedContext };
    updateMessages((prev) => [...prev, userMessage]);

    // Clear input and set generating state
    setContext('');
    if (setGenerating) {
      setGenerating(true);
    }

    try {
      // Prepare the payload for the backend
      const sessionUser = getStoredUser();
      if (!sessionUser?.email) {
        redirectToLoginWithReturn({
          message: 'Votre session a expire. Veuillez vous reconnecter.',
          publishLogoutReason: 'session_expired',
        });
        throw new Error('Not authenticated');
      }
      const payload = {
        userEmail: sessionUser.email,
        message: [normalizedContext, normalizedAiContext].filter(Boolean).join(' '),
        conversation_history: messages || [],
        user_context: {
          user_id: sessionUser.id,
          user_email: sessionUser.email,
          user_name: `${sessionUser.first_name} ${sessionUser.last_name}`,
          user_role: sessionUser.role?.name,
          current_page: currentPage,
          priority_school: sessionUser.selectedPrioritySchool,
          ...userInfo,
        },
        session_context: {
          timestamp: new Date().toISOString(),
          has_files: !!(selectedFile || selectedImage),
          file_info: selectedFile
            ? {
                name: selectedFile.name,
                type: selectedFile.type,
                size: selectedFile.size,
              }
            : null,
          essay_analysis: sessionContext.essay_analysis || null,
          cv_analysis: sessionContext.cv_analysis || null,
          gmat_analysis: sessionContext.gmat_analysis || null,
          english_analysis: sessionContext.english_analysis || null,
          interview_analysis: sessionContext.interview_analysis || null,
          profile_analysis: sessionContext.profile_analysis || null,
          image_info: selectedImage
            ? {
                name: selectedImage.name,
                type: selectedImage.type,
                size: selectedImage.size,
              }
            : null,
          ...sessionContext,
        },
        tools_available: [
          'web_search',
          'school_information',
          'profile_analysis',
          'essay_feedback',
          'interview_prep',
          'test_prep_guidance',
          'application_timeline',
          'document_analysis',
        ],
      };

      const response = await fetch(`${API_BASE_URL}/api/copilot-message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.status === 401) {
        redirectToLoginWithReturn({
          message: 'Votre session a expire. Veuillez vous reconnecter.',
          publishLogoutReason: 'session_expired',
        });
        throw new Error('Not authenticated');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Add AI response to chat
      const aiMessage = {
        role: 'ai',
        content:
          data.response ||
          "I apologize, but I couldn't process your request at the moment. Please try again.",
        tool_calls: data.tool_calls || [],
        metadata: data.metadata || {},
      };
      updateMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message to copilot:', error);

      // Add error message to chat
      const errorMessage = {
        role: 'ai',
        content:
          error.message === 'Not authenticated'
            ? 'Please log in again to use Copilot.'
            : "I apologize, but I'm experiencing technical difficulties. Please try again in a moment.",
      };

      updateMessages((prev) => [...prev, errorMessage]);
    } finally {
      if (setGenerating) {
        setGenerating(false);
      }
    }
  };

  useEffect(() => {
    if (messagesEndRef && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Load saved conversation when component mounts (only if no messages exist)
  useEffect(() => {
    if ((!messages || messages.length === 0) && setMessages) {
      try {
        const sessionUser = JSON.parse(sessionStorage.getItem('user'));
        const prioritySchool =
          localStorage.getItem('selectedPrioritySchool') || 'general';
        if (sessionUser?.email) {
          const savedMessages = loadCopilotConversation(
            sessionUser.email,
            prioritySchool
          );
          if (savedMessages.length > 0) {
            setMessages(savedMessages);
          }
        }
      } catch (error) {
        console.error('Error loading saved conversation:', error);
      }
    }
  }, [setMessages]); // Simplified dependencies

  // WebSocket connection for real-time messages
  useEffect(() => {
    const sessionUser = getStoredUser();

    if (!hasAuthenticatedSession() || !sessionUser?.email || !setMessages) {
      return;
    }

    let socket = null;
    let isConnecting = false;
    let reconnectTimeoutId = null;
    let intentionallyClosed = false;

    const connectWebSocket = () => {
      if (
        intentionallyClosed ||
        isConnecting ||
        (socket &&
          (socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING))
      ) {
        return;
      }

      isConnecting = true;

      try {
        socket = createAuthenticatedWebSocket(`${API_SOCKET_URL}/api/ws/tasks`);
        if (!socket) {
          isConnecting = false;
          return;
        }

        socket.onopen = () => {
          isConnecting = false;
        };

        socket.onmessage = (event) => {
          const messageContent = event.data;

          updateMessages((prev) => {
            // Avoid adding duplicate messages
            if (
              prev.length === 0 ||
              prev[prev.length - 1].content !== messageContent
            ) {
              return [...prev, { role: 'ai', content: messageContent }];
            }
            return prev;
          });
        };

        socket.onerror = (error) => {
          if (!intentionallyClosed) {
            console.error('Copilot WebSocket error:', error);
          }
          isConnecting = false;
        };

        socket.onclose = (event) => {
          isConnecting = false;

          // Reconnect if not intentionally closed
          if (!intentionallyClosed && event.code !== 1000 && event.code !== 1001) {
            reconnectTimeoutId = setTimeout(connectWebSocket, 3000);
          }
        };
      } catch (error) {
        console.error('WebSocket connection failed:', error);
        isConnecting = false;
      }
    };

    connectWebSocket();

    return () => {
      intentionallyClosed = true;
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }
      if (socket?.readyState === WebSocket.OPEN) {
        socket.close(1000, 'Component unmounting');
      } else if (socket?.readyState === WebSocket.CONNECTING) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
      }
    };
  }, [updateMessages]); // Only depend on updateMessages to avoid reconnections

  return (
    <div
      className={
        lgSize ? 'ai-copilot cursor-style-lg' : 'ai-copilot cursor-style'
      }
    >
      <div className="copilot-header">
        <div className="copilot-header-brand">
          <img
            src={liwazaLogoWhiteShort}
            alt="Liwaza Logo"
            className="copilot-logo"
          />
          <h2>Liwaza AI</h2>
        </div>
        <button
          className="close-btn"
          onClick={onClose}
          aria-label="Close Copilot"
        >
          <FiMinus />
        </button>
      </div>
      <div className="copilot-messages">
        {messages.map((msg, i) => {
          // Check if the message contains HTML tags
          const containsHtml =
            typeof msg.content === 'string' && /<[^>]*>/.test(msg.content);

          // If the message looks like raw HTML (e.g., profile assessment), render as-is
          const isRawHtml =
            typeof msg.content === 'string' &&
            msg.content
              .trim()
              .startsWith('<div class="copilot-card-highlighter"');

          if (msg.role === 'user') {
            return (
              <div key={i} className="copilot-message user">
                {msg.content}
              </div>
            );
          }

          return (
            <div
              key={i}
              className={`copilot-message ${msg.role}`}
              dangerouslySetInnerHTML={{
                __html:
                  isRawHtml || containsHtml
                    ? convertCustomTextToHTML(msg.content)
                    : convertCustomTextToHTML(msg.content),
              }}
            />
          );
        })}
        <div ref={messagesEndRef} />
      </div>
      {generating && (
        <div className="generating-banner">
          <span
            style={{
              opacity: textFading ? 0 : 1,
              transition: 'opacity 0.6s ease-in-out',
            }}
          >
            {generatingText}
          </span>
          <span className="dots">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        </div>
      )}
      <form className="copilot-input-area" onSubmit={(e) => handleSubmit(e)}>
        <div className="input-row">
          <textarea
            className="copilot-textarea"
            placeholder="Ask for something..."
            value={context}
            onChange={(e) => setContext(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            rows={3}
          />
          <button type="submit" className="send-btn">
            <FiSend size={20} />
          </button>
        </div>
        <div className="attachment-options">
          {showFileUpload && (
            <label className="attachment-btn">
              <FiUpload size={20} />
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileUpload}
                hidden
              />
            </label>
          )}
          {showImageUpload && (
            <label className="attachment-btn">
              <FiImage size={20} />
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                hidden
              />
            </label>
          )}
        </div>
        {selectedFile && (
          <div className="attachment-preview">
            <FiFileText size={20} />
            <span>{selectedFile.name}</span>
          </div>
        )}
        {selectedImage && (
          <div className="attachment-preview">
            <FiImage size={20} />
            <span>{selectedImage.name}</span>
          </div>
        )}
      </form>
    </div>
  );
};

export default Copilot;
